/**
 * VAR Service Adapter
 *
 * Frontend service adapter that calls the backend VAR API
 * Issue: #289
 */

import { EntityServiceAdapter, QueryParams, QueryResult } from '../../../types/baseExplorerConfig';
import { VAREntity, VARFilters } from '../../../types/var';
import { VAREntityEntry } from './varConfig';
import { logger } from '../../../utils/logger';
import { getBackendUrl, isDevMode } from '../../../services/configService';
import { objectToSearchParams } from '../baseExplorer/objectToSearchParams';
import { backendPage } from '../../../utils/pagination';
import { translateVarParams } from './varParams';

const log = logger('VARServiceAdapter');

/**
 * Get the base URL for VAR API
 */
function getBaseUrl(): string {
  const backendUrl = getBackendUrl();
  if (!backendUrl) {
    throw new Error('BACKEND_URL not configured in runtime config');
  }
  return `${backendUrl}/api/var`;
}

/**
 * Get authentication token from local storage
 */
function getAuthToken(): string | null {
  const accessToken = localStorage.getItem('access_token');

  if (!accessToken) {
    if (isDevMode()) {
      return 'dev-mode-token';
    } else {
      log.debug('No access token found in production mode');
      return null;
    }
  }
  return accessToken;
}

/**
 * Map of CBE filter field names to backend query param names. Most VAR
 * filters use the same name in both contexts; sprinklerFlag is the only
 * exception (renamed to sprinkler_active for the backend).
 */
/**
 * Build headers with authentication
 */
function buildHeaders(): HeadersInit {
  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  const token = getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

/**
 * VAR Service Adapter for ConfigurableBaseExplorer
 */
export const varServiceAdapter: EntityServiceAdapter<VAREntityEntry> = {
  /**
   * Fetch VAR data with pagination and filtering
   */
  async fetchData(params: QueryParams): Promise<QueryResult<VAREntityEntry>> {
    log.debug('VAR ServiceAdapter: fetchData called', params);

    try {
      const queryParams = new URLSearchParams();

      // Pagination
      const { page, limit } = backendPage(params);
      queryParams.append('page', String(page));
      queryParams.append('limit', String(limit));

      // Search, sort, and all filters via shared translator
      const translated = translateVarParams(params);
      for (const [key, value] of Object.entries(translated)) {
        if (value == null || value === '') continue;
        queryParams.append(key, String(value));
      }

      const url = `${getBaseUrl()}?${queryParams.toString()}`;
      log.debug('VAR ServiceAdapter: API URL', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: buildHeaders(),
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      log.debug(`VAR ServiceAdapter: Received ${result.data?.length || 0} VAR points, ${result.total} total`);

      // Convert to ConfigurableBaseExplorer format
      return {
        data: (result.data || []).map((var_point: VAREntity) => ({
          ...var_point,
          _id: var_point.id // Map id to _id for BaseEntity compatibility
        })),
        total: result.total || 0,
        page: result.page || 1,
        limit: result.limit || 25,
        hasMore: (result.page * result.limit) < result.total
      };

    } catch (error) {
      log.error('VAR ServiceAdapter: Fetch error', error);
      throw error;
    }
  },

  buildExportParams: (params: QueryParams) => objectToSearchParams(translateVarParams(params)),

  /**
   * Create new VAR entity
   */
  async createEntity(data: Omit<VAREntityEntry, '_id'>): Promise<VAREntityEntry> {
    log.debug('VAR ServiceAdapter: Creating VAR', data);

    try {
      const response = await fetch(getBaseUrl(), {
        method: 'POST',
        headers: buildHeaders(),
        body: JSON.stringify(data),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`HTTP ${response.status}: ${errorData.message || response.statusText}`);
      }

      const result = await response.json();
      return {
        ...result.data,
        _id: result.data.id
      };

    } catch (error) {
      log.error('VAR ServiceAdapter: Create error', error);
      throw error;
    }
  },

  /**
   * Update existing VAR entity
   */
  async updateEntity(id: string, updates: Partial<VAREntityEntry>): Promise<VAREntityEntry> {
    log.debug('VAR ServiceAdapter: Updating VAR', id, updates);

    try {
      const response = await fetch(`${getBaseUrl()}/${id}`, {
        method: 'PUT',
        headers: buildHeaders(),
        body: JSON.stringify(updates),
        credentials: 'include'
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error(`VAR with ID ${id} not found`);
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`HTTP ${response.status}: ${errorData.message || response.statusText}`);
      }

      const result = await response.json();
      return {
        ...result.data,
        _id: result.data.id
      };

    } catch (error) {
      log.error('VAR ServiceAdapter: Update error', error);
      throw error;
    }
  },

  /**
   * Delete VAR entity
   */
  async deleteEntity(id: string): Promise<boolean> {
    log.debug('VAR ServiceAdapter: Deleting VAR', id);

    try {
      const response = await fetch(`${getBaseUrl()}/${id}`, {
        method: 'DELETE',
        headers: buildHeaders(),
        credentials: 'include'
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error(`VAR with ID ${id} not found`);
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      log.debug('VAR ServiceAdapter: Deleted VAR', id);
      return true;

    } catch (error) {
      log.error('VAR ServiceAdapter: Delete error', error);
      throw error;
    }
  }
};

export default varServiceAdapter;
